import type {
  AlignerFormData,
  BleachingTrayParameters,
  CaseParameters,
  DateFormat,
  DecoratedFileCollection,
  DecoratedFileCollectionFile,
  Dictionary,
  FileCollectionTypeId,
  FileTypeId,
  NightguardParameters,
  Nullish,
  OrthoParameters,
  RetainerParameters,
  ScanServiceType,
  SelectedFile,
  SelectedFiles,
  SelectedFileType,
  SelectedTeeth,
  UndecoratedFile,
  UndecoratedFileCollection,
  UndecoratedFileVersion,
  UndecoratedUnisonProject,
  UnisonProjectTypeId,
} from '@witmetrics/utils';
import {
  FILE_COLLECTION_TYPE_ID,
  FILE_TYPES,
  UNISON_PROJECT_TYPE_ID,
  UPLOAD_STATUSES,
} from '@witmetrics/utils/constants';
import { excludeKey } from '@witmetrics/utils/objects';
import { currentDate, formatTextDate } from '@witmetrics/utils/time';
import {
  getServiceScansCount,
  isBleachingTrayService,
  isNightguardService,
  isOrthoService,
  isRetainerService,
} from '@witmetrics/utils/scanServices';
import { isBlank, toTitleCase } from '@witmetrics/utils/strings';
import { isPdf, isScan, isViewableNonScan } from '@witmetrics/utils/files';
import { isEmpty, isNullish } from '@witmetrics/utils/misc';
import { asyncMap } from '@witmetrics/utils/arrays';
import {
  BLEACHING_FLOAT_PARAMS,
  type BleachingFormParams,
  type FloatFieldValue,
  type FormParameters,
  NIGHTGUARD_FLOAT_PARAMS,
  type NightguardFormParams,
  ORTHO_FLOAT_PARAMS,
  type OrthoFormParams,
  RETAINER_FLOAT_PARAMS,
  type RetainerFormParams,
} from '@witmetrics/unison-core';
import { loadScan } from '@/utils/scans';
import type { LoadedFile } from '@/providers/LoadedFilesProvider';
import { uploadFile } from '@/utils/files';
import { API } from '@/api';
import type {
  CreateReluServiceParams,
  MergedUploadedFiles,
  NewProjectParams,
  ServiceRequest,
  UploadedFileData,
  UploadProgressHandler,
  WizardStep,
} from './types';
import { RELU_LOWER_ARCH, RELU_UPPER_ARCH, WIZARD_STEPS } from './constants';

const { IN_PROGRESS, DONE, ERROR } = UPLOAD_STATUSES;
const { SELECT_PATIENT, SELECT_FILES } = WIZARD_STEPS;
const { DOCUMENTS, GENERAL } = FILE_COLLECTION_TYPE_ID;

const { UPPER_ARCH, LOWER_ARCH } = FILE_TYPES;

export const classes = {
  dialog:
    'w-[95vw] max-w-[1200px] min-w-[400px] min-h-[450px] max-h-[750px] h-[95vh] flex flex-col',
  dialogTitle: 'mb-0 pb-2',
  aiIcon: 'mr-2 text-2xl',
  body: 'grow pt-0 pb-4 min-h-0 w-full',
  wrapper: 'flex justify-between size-full',
  form: 'flex flex-col size-full overflow-y-auto pt-2 px-4 pb-4',
  formWithSettings: 'w-2/5',
  nameSection: 'mt-6 mb-6',
  dropdown: 'w-full mb-4',
  rowDivider: 'mb-4 mt-0',
  columnDivider: 'mx-8 my-0',
  section: 'mb-4',
  settingsToggle: 'mb-4',
};

export function hasSelectedFiles(selectedFiles: SelectedFiles) {
  return Object.values(selectedFiles).some((files) => files.length > 0);
}

function hasBothArches(selectedFiles: SelectedFiles) {
  // Has both arches selected (required for Michigan splint / nightguard services)
  return !(
    isEmpty(selectedFiles.RELU_UPPER_ARCH) ||
    isEmpty(selectedFiles.RELU_LOWER_ARCH)
  );
}

function hasArch(selectedFiles: SelectedFiles) {
  // Has at least one arch selected for other services
  return !(
    isEmpty(selectedFiles.RELU_UPPER_ARCH) &&
    isEmpty(selectedFiles.RELU_LOWER_ARCH)
  );
}

export function isSaveEnabled(
  step: WizardStep,
  projectTypeId: UnisonProjectTypeId | undefined,
  serviceType: ScanServiceType | undefined,
  selectedFiles: SelectedFiles,
  leadId: number | undefined,
  params: FormParameters,
  notes: string
) {
  if (step === SELECT_PATIENT) {
    return !!leadId; // Need to have a patient selected
  } else if (step === SELECT_FILES) {
    if (!serviceType) return true; // Non-AI cases have no file requirements
    return isNightguardService(serviceType)
      ? hasBothArches(selectedFiles)
      : hasArch(selectedFiles);
  }
  // eslint-disable-next-line witmetrics/no-multiline-comments
  /* if (step === CASE_SETTINGS && projectTypeId === ALIGNERS) {
  // Need all fields filled in for aligner design
  // return !Object.values(params).includes(null);
  // }
  // if (step === REVIEW && projectTypeId === ALIGNERS) {
  //   return !isEmpty(notes);
  // }
   */
  return true; // TODO: Any other edge cases?
}

export function fetchTokenCount(serviceType: ScanServiceType) {
  return async (
    params: FormParameters,
    selectedFiles: SelectedFiles,
    currentTokenCount: number
  ) => {
    if (!serviceType) return 0; // Any of the non-AI services
    // eslint-disable-next-line witmetrics/no-multiline-comments
    /* TODO: Check if params that change cost have been updated before making API call
        If not, just return currentTokenCount
     */
    return (
      await API.ScanServices.fetchScanServiceCost(
        serviceType,
        getServiceScansCount(selectedFiles),
        parseFormData(serviceType, params)
      )
    ).cost.total;
  };
}

export async function createReluService({
  projectId,
  serviceType,
  params,
  selectedFiles,
  parentCollection,
  inputFilesCollection,
  onUploadProgress,
}: CreateReluServiceParams) {
  // Upload upper/lower scans (if present) to Relu & our API. Scans need to get added to the "Input Files" collection.
  const upperScan = selectedFiles.RELU_UPPER_ARCH[0] || null;
  const lowerScan = selectedFiles.RELU_LOWER_ARCH[0] || null;
  const { upperJaw, lowerJaw } = await uploadInputs(
    upperScan,
    lowerScan,
    inputFilesCollection,
    onUploadProgress
  );

  let uploads: UploadedFileData[] = [];
  let data: ServiceRequest = { name: buildServiceName(upperScan, lowerScan) };
  // Only send files that exist to createService
  if (upperJaw) {
    uploads.push(upperJaw.fileData);
    data.upperJaw = excludeKey(upperJaw, 'fileData');
  }
  if (lowerJaw) {
    uploads.push(lowerJaw.fileData);
    data.lowerJaw = excludeKey(lowerJaw, 'fileData');
  }
  // Initiate a scanService based on project type
  let service = await createService(serviceType, data, params);
  // Add service to project, add file collection to service
  const [projectService, serviceCollection] = await Promise.all([
    addServiceToProject(projectId, service.id),
    API.ScanServices.addFileCollectionToScanService(
      service.id,
      parentCollection.id
    ),
  ]);
  // Return everything to dispatch to Redux
  return {
    service,
    projectService,
    serviceCollection,
    uploads: parseUploadArray(uploads),
  };
}

function buildServiceName(
  upperScan: SelectedFile | Nullish,
  lowerScan: SelectedFile | Nullish
) {
  let names = [];
  if (upperScan) names.push(upperScan.file.name);
  if (lowerScan) names.push(lowerScan.file.name);
  return names.join(', ');
}

async function uploadInputs(
  upperScan: SelectedFile | Nullish,
  lowerScan: SelectedFile | Nullish,
  inputFilesCollection: DecoratedFileCollection,
  onUploadProgress: UploadProgressHandler
) {
  const [upperJaw, lowerJaw] = await Promise.all([
    processInputFile(
      upperScan,
      UPPER_ARCH.name,
      inputFilesCollection,
      onUploadProgress
    ),
    processInputFile(
      lowerScan,
      LOWER_ARCH.name,
      inputFilesCollection,
      onUploadProgress
    ),
  ]);
  return { upperJaw, lowerJaw };
}

function createService(
  serviceType: ScanServiceType,
  data: ServiceRequest,
  params: FormParameters
) {
  const parsed = parseFormData(serviceType, params);
  // Project type will determine which service to create
  if (isRetainerService(serviceType)) {
    return createRetainerService(data, parsed as RetainerParameters);
  } else if (isNightguardService(serviceType)) {
    return createNightguardService(data, parsed as NightguardParameters);
  } else if (isBleachingTrayService(serviceType)) {
    return createBleachingTrayService(data, parsed as BleachingTrayParameters);
  } else if (isOrthoService(serviceType)) {
    return createOrthoService(data, parsed as OrthoParameters);
  } else {
    throw new Error(`Unsupported service type for AI service: ${serviceType}`);
  }
}

function createRetainerService(
  data: ServiceRequest,
  parameters: RetainerParameters
) {
  return API.ScanServices.createNewRetainerService({ ...data, parameters });
}

function createNightguardService(
  data: ServiceRequest,
  parameters: NightguardParameters
) {
  return API.ScanServices.createNewNightguardService({ ...data, parameters });
}

function createBleachingTrayService(
  data: ServiceRequest,
  parameters: BleachingTrayParameters
) {
  return API.ScanServices.createNewBleachingTrayService({
    ...data,
    parameters,
  });
}

function createOrthoService(data: ServiceRequest, parameters: OrthoParameters) {
  return API.ScanServices.createNewOrthodonticModelService({
    ...data,
    parameters,
  });
}

export function addServiceToProject(projectId: number, scanServiceId: number) {
  return API.UnisonProjects.addScanServiceToUnisonProject(
    projectId,
    scanServiceId
  );
}

async function processInputFile(
  selectedFile: SelectedFile | Nullish,
  fileType: SelectedFileType,
  parentCollection: DecoratedFileCollection,
  onUploadProgress: UploadProgressHandler
) {
  // Upload an upper/lower arch (if present) to our API & Relu
  if (!selectedFile) return undefined;
  const { uploadUrl, fileId, extension } =
    await API.ScanServices.createUploadUrl(selectedFile.file.name);
  const [fileData] = await Promise.all([
    uploadFileToCollection(
      parentCollection,
      fileType,
      selectedFile,
      onUploadProgress
    ),
    API.ScanServices.uploadFileToUrl(uploadUrl, selectedFile.file),
  ]);
  return { fileId, extension, fileData };
}

export async function createNewProject(params: NewProjectParams) {
  // Create the project
  const unisonProject = await createProject(params);
  // Optionally create notes (null if blank)
  const unisonProjectNote = await createProjectNote(
    unisonProject.id,
    params.notes
  );
  return { unisonProject, unisonProjectNote };
}

function createProject({
  name,
  externalRef,
  description,
  userId,
  projectTypeId,
}: NewProjectParams) {
  return API.UnisonProjects.createNewProject({
    name,
    externalRef,
    description,
    userId,
    unisonProjectTypeId: projectTypeId,
  });
}

export async function createProjectFileCollection(
  project: UndecoratedUnisonProject,
  selectedFiles: Partial<SelectedFiles>,
  onUploadProgress: UploadProgressHandler,
  dateFormat?: DateFormat,
  serviceType?: ScanServiceType
) {
  const { name, practiceId } = project;
  // Create a top-level collection to hold everything
  const [generalCollection, documentsCollection] = await Promise.all([
    createFileCollection({
      name: `${name} (general)`,
      practiceId,
      fileCollectionTypeId: GENERAL,
    }),
    createFileCollection({
      name: `${name} (documents)`,
      practiceId,
      fileCollectionTypeId: DOCUMENTS,
    }),
  ]);
  // Link the top-level collections with the project
  const projectFileCollections = await addCollectionsToProject(
    project.id,
    generalCollection.id,
    documentsCollection.id
  );
  // Split documents & non-document files
  const { DOCUMENT, ...nonDocFiles } = selectedFiles;
  // Upload all the files (besides scan service ones) & add to top-level collection
  const [docUploads, nonDocUploads] = await Promise.all([
    uploadCaseFiles(documentsCollection, { DOCUMENT }, onUploadProgress),
    uploadCaseFiles(generalCollection, nonDocFiles, onUploadProgress),
  ]);
  // Start an array of new collection(s)
  let fileCollections: DecoratedFileCollection[] = [
    {
      ...generalCollection,
      childFileCollections: 0,
      childFiles: nonDocUploads.length,
    },
    {
      ...documentsCollection,
      childFileCollections: 0,
      childFiles: docUploads.length,
    },
  ];
  let serviceCollection: DecoratedFileCollection | undefined,
    inputFilesCollection: DecoratedFileCollection | undefined;
  if (serviceType) {
    fileCollections[0].childFileCollections = 1; // 1 for the scanService collection
    // Create a collection to hold the scan service files
    serviceCollection = await createFileCollection({
      name: buildServiceCollectionName(serviceType, dateFormat),
      practiceId,
      parentFileCollectionId: generalCollection.id,
    });
    // Add a collection to hold the input files & add to the service collection
    inputFilesCollection = await createFileCollection({
      name: 'Input Files',
      practiceId,
      parentFileCollectionId: serviceCollection.id,
    });
    fileCollections.push(serviceCollection, inputFilesCollection);
  }
  return {
    unisonProjectFileCollections: [
      { ...projectFileCollections[0], fileCollection: fileCollections[0] },
      { ...projectFileCollections[1], fileCollection: fileCollections[1] },
    ],
    fileCollections,
    serviceCollection, // Will need for linking collection to the scanService later
    inputFilesCollection, // Will need for uploading input files later
    ...parseUploadArray([...docUploads, ...nonDocUploads]),
  };
}

function buildServiceCollectionName(
  serviceType: ScanServiceType,
  dateFormat?: DateFormat
) {
  // e.g. "Retainers (15 Oct 2025)"
  let type: string;
  if (isRetainerService(serviceType)) {
    type = 'Retainers service';
  } else if (isNightguardService(serviceType)) {
    type = 'Nightguard service';
  } else if (isBleachingTrayService(serviceType)) {
    type = 'Bleaching tray service';
  } else if (isOrthoService(serviceType)) {
    type = 'Orthodontic model service';
  } else {
    type = toTitleCase(
      (serviceType as string).replace(/_/g, ' ').toLowerCase()
    );
  }
  return `${type} (${formatTextDate(currentDate(), dateFormat)})`;
}

async function createProjectNote(projectId: number, content: string) {
  // Create a note if treatment goals are present (required for aligners)
  if (isBlank(content)) return null;
  const note = await API.Notes.createNewNote({ content });
  return API.UnisonProjects.addNoteToUnisonProject(projectId, note.id);
}

async function createFileCollection({
  name,
  practiceId,
  parentFileCollectionId,
  fileCollectionTypeId,
}: {
  name: string;
  practiceId: number;
  parentFileCollectionId?: number;
  fileCollectionTypeId?: FileCollectionTypeId;
}) {
  const collection = await API.FileCollections.createNewFileCollection({
    name,
    practiceId,
    parentFileCollectionId,
    fileCollectionTypeId,
  });
  return {
    ...collection,
    childFileCollections: 0,
    childFiles: 0,
  } as DecoratedFileCollection;
}

function addCollectionsToProject(
  projectId: number,
  generalId: number,
  documentsId: number
) {
  // Link the top-level collection with the project
  return Promise.all([
    API.UnisonProjects.addFileCollectionToUnisonProject(projectId, generalId),
    API.UnisonProjects.addFileCollectionToUnisonProject(projectId, documentsId),
  ]);
}

async function uploadCaseFiles(
  parentFileCollection: DecoratedFileCollection,
  selectedFiles: Partial<SelectedFiles>,
  onUploadProgress: UploadProgressHandler
) {
  return (
    await asyncMap(
      Object.keys(selectedFiles) as SelectedFileType[],
      (fileType) =>
        // Upload all the files of a given fileType
        asyncMap(selectedFiles[fileType]!, (selectedFile) =>
          uploadFileToCollection(
            parentFileCollection,
            fileType,
            selectedFile,
            onUploadProgress
          )
        )
    )
  ).flat();
}

async function uploadFileToCollection(
  collection: Pick<UndecoratedFileCollection, 'id' | 'practiceId'>,
  fileType: SelectedFileType,
  selectedFile: SelectedFile,
  onUploadProgress: UploadProgressHandler
): Promise<UploadedFileData> {
  const { id: collectionId, practiceId } = collection;
  try {
    if (isNullish(practiceId)) {
      throw new Error(`File collection ${collectionId} has no practiceId`);
    }
    onUploadProgress(fileType, { ...selectedFile, uploadStatus: IN_PROGRESS });
    let fileTypeId: FileTypeId;
    if (fileType === RELU_UPPER_ARCH) {
      fileTypeId = FILE_TYPES.UPPER_ARCH.id;
    } else if (fileType === RELU_LOWER_ARCH) {
      fileTypeId = FILE_TYPES.LOWER_ARCH.id;
    } else {
      fileTypeId = FILE_TYPES[fileType].id;
    }
    const { file, fileVersion } = await uploadFile(
      selectedFile.file,
      practiceId,
      fileTypeId
    );
    const fileCollectionFile = await API.FileCollections.addFileCollectionFile(
      collectionId,
      file.id
    );
    // Load file locally, no need to download right after uploading
    let loadedFile: LoadedFile | undefined;
    const buffer = await selectedFile.file.arrayBuffer();
    if (isScan(fileVersion.name)) {
      loadedFile = loadScan(fileVersion.name, buffer, fileTypeId)!;
    } else if (isPdf(fileVersion.name)) {
      loadedFile = URL.createObjectURL(
        new Blob([buffer], { type: 'application/pdf' })
      );
    } else if (isViewableNonScan(fileVersion.name)) {
      loadedFile = URL.createObjectURL(new Blob([buffer]));
    }
    onUploadProgress(fileType, { ...selectedFile, uploadStatus: DONE });
    return {
      file,
      fileVersion,
      fileCollectionFile,
      loadedFile,
      loadedPreview: selectedFile.previewSource,
    };
  } catch (err) {
    onUploadProgress(fileType, { ...selectedFile, uploadStatus: ERROR });
    throw err;
  }
}

function parseUploadArray(uploads: UploadedFileData[]): MergedUploadedFiles {
  let files: UndecoratedFile[] = [];
  let fileVersions: UndecoratedFileVersion[] = [];
  let fileCollectionFiles: DecoratedFileCollectionFile[] = [];
  let loadedFiles: Dictionary<LoadedFile> = {};
  uploads.flat().forEach((u) => {
    files.push(u.file);
    fileVersions.push(u.fileVersion);
    fileCollectionFiles.push(u.fileCollectionFile);
    if (u.loadedFile) loadedFiles[u.file.id] = u.loadedFile;
    if (u.loadedPreview) {
      loadedFiles[`preview-${u.fileVersion.id}`] = u.loadedPreview;
    }
  });
  return { files, fileVersions, fileCollectionFiles, loadedFiles };
}

export function parseFormData(
  serviceType: ScanServiceType,
  params: FormParameters
): CaseParameters {
  if (isBleachingTrayService(serviceType)) {
    return parseBleachingForm(params as BleachingFormParams);
  } else if (isNightguardService(serviceType)) {
    return parseNightguardForm(params as NightguardFormParams);
  } else if (isOrthoService(serviceType)) {
    return parseOrthoForm(params as OrthoFormParams);
  } else if (isRetainerService(serviceType)) {
    return parseRetainerForm(params as RetainerFormParams);
  } else return params as AlignerFormData;
}

function parseBleachingForm(
  params: BleachingFormParams
): BleachingTrayParameters {
  return (Object.keys(params) as (keyof BleachingFormParams)[]).reduce(
    (a, b) => {
      return {
        ...a,
        [b]: BLEACHING_FLOAT_PARAMS.includes(b)
          ? (params[b] as FloatFieldValue).value
          : params[b],
      };
    },
    {} as Partial<BleachingTrayParameters>
  ) as BleachingTrayParameters;
}

function parseNightguardForm(
  params: NightguardFormParams
): NightguardParameters {
  return (Object.keys(params) as (keyof NightguardFormParams)[]).reduce(
    (a, b) => {
      let val = NIGHTGUARD_FLOAT_PARAMS.includes(b)
        ? (params[b] as FloatFieldValue).value
        : params[b];
      // NOTE: `baseHeight` needs to be converted back to string before saving
      if (b === 'baseHeight') val = (val as number).toString();
      return { ...a, [b]: val };
    },
    {} as Partial<NightguardParameters>
  ) as NightguardParameters;
}

function parseOrthoForm(params: OrthoFormParams): OrthoParameters {
  return (Object.keys(params) as (keyof OrthoFormParams)[]).reduce((a, b) => {
    return {
      ...a,
      [b]: ORTHO_FLOAT_PARAMS.includes(b)
        ? (params[b] as FloatFieldValue).value
        : params[b],
    };
  }, {} as Partial<OrthoParameters>) as OrthoParameters;
}

function parseRetainerForm(params: RetainerFormParams): RetainerParameters {
  return (Object.keys(params) as (keyof RetainerFormParams)[]).reduce(
    (a, b) => {
      return {
        ...a,
        [b]: RETAINER_FLOAT_PARAMS.includes(b)
          ? (params[b] as FloatFieldValue).value
          : params[b],
      };
    },
    {} as Partial<RetainerParameters>
  ) as RetainerParameters;
}

export function buildNotes(
  treatmentGoals: string,
  params: FormParameters,
  selectedTeeth: SelectedTeeth,
  projectTypeId: UnisonProjectTypeId | Nullish,
  isPreVeneers: boolean
) {
  // TODO: Notes for non-aligner cases
  const teeth = Object.values(selectedTeeth).flat();

  if (projectTypeId !== UNISON_PROJECT_TYPE_ID.ALIGNERS) {
    return `**Treatment Goals:**\n${isBlank(treatmentGoals) ? 'Not specified' : treatmentGoals.trim()}`;
  }

  const formatValue = (param: keyof AlignerFormData) => {
    return (params as AlignerFormData)[param] || '--';
  };

  return `**Treatment Goals:**
${treatmentGoals}

**Pre-veneers:** ${isPreVeneers ? 'Yes' : 'No'}


**Unmovable teeth:** ${teeth.length > 0 ? teeth.join(', ') : '--'}

  
**Arch to treat:** ${formatValue('archToTreat')}


**Spacing:** ${formatValue('spacing')}


**Midline:** ${formatValue('midline')}


**Overjet:** ${formatValue('overjet')}


**Overbite:** ${formatValue('overbite')}


**Posterior crossbite:** ${formatValue('posteriorCrossbite')}`;
}
